import { formatAmounts, pluralize } from '@aytm/app/stats/lib/util/helpers'
import { buildBaseConfig } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/baseConfig'
import { linkRowFromPoint } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/pointHalo'
import { sortData } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/sortData'
import { removeLogicAndTruncate } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/textUtils'
import { wrapTooltip } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/tooltipFormatter'
import { CHART_HEIGHT } from '@aytm/app/stats/page/question-views/components/lib/constants'

const SCATTER_MARGIN = [84, 37, 65, 85]

const checkChartExtremes = chart => {
  const visibleS = chart.series.filter(s => s.visible)
  if (visibleS.length < 2 && chart.xAxis[0].getExtremes().min < 0) {
    chart.xAxis[0].setExtremes(0.2, null, true)
  }
}

const buildScatterIngot = () => {
  return buildBaseConfig({
    chart: {
      animation: false,
      backgroundColor: 'transparent',
      inverted: false,
      margin: [...SCATTER_MARGIN],
      type: 'scatter',
      events: {
        load: function () {
          const chart = this
          for (const s of chart.series) {
            if (!s.visible) {
              // HC 10.3+ moved series.legendGroup/legendItem SVG elements into
              // the legendItem object ({ group, label }); ported from coffee as-is
              // this crashed whenever an answer had no reaction time
              const group = s.legendItem?.group?.element
              const label = s.legendItem?.label?.element
              if (group) {
                group.onmouseover = null
                group.onclick = null
              }
              if (label) {
                label.onclick = null
                label.style.cursor = 'default'
              }
            }
          }
          checkChartExtremes(chart)
        },
        redraw: function () {
          checkChartExtremes(this)
        }
      }
    },
    plotOptions: {
      scatter: {
        dataLabels: {
          align: 'center',
          color: '#666666',
          enabled: false,
          formatter() {
            return this.point.name
          },
          padding: 10
        },
        fillOpacity: 0.9,
        lineWidth: 0,
        shadow: false
      },
      series: {
        animation: false,
        borderWidth: 1,
        marker: {
          symbol: 'circle',
          radius: 5,
          lineWidth: 0
        },
        shadow: false,
        states: {
          hover: { brightness: 0.05 },
          inactive: { opacity: 1 }
        },
        point: {
          events: {
            mouseOut() {
              linkRowFromPoint(this.series?.chart, null)
            },
            mouseOver() {
              linkRowFromPoint(this.series?.chart, this.id)
            }
          }
        }
      }
    },
    tooltip: {
      formatter() {
        return wrapTooltip(this.point.tooltip)
      }
    },
    xAxis: {
      title: {
        color: '#4896BC',
        text: '',
        style: { font: '12px Ubuntu' },
        offset: 30,
        reserveSpace: true
      },
      minorGridLineWidth: 1,
      gridLineWidth: 1,
      lineColor: '#999',
      lineWidth: 1,
      startOnTick: true,
      endOnTick: true,
      maxPadding: 0,
      minPadding: 0,
      floor: 0,
      tickAmount: 24,
      tickColor: '#e9e9e9',
      labels: {
        autoRotation: 0,
        autoRotationLimit: 0,
        formatter() {
          return this.value.toFixed(2)
        },
        step: 4
      }
    },
    yAxis: {
      labels: {
        autoRotation: 0,
        autoRotationLimit: 0
      },
      max: 100,
      min: 0,
      title: {
        color: '#4896BC',
        text: 'percents',
        style: { font: '12px Ubuntu' },
        offset: 40
      },
      minorGridLineWidth: 1,
      gridLineWidth: 1,
      lineColor: '#999',
      lineWidth: 1,
      endOnTick: true,
      startOnTick: true,
      tickAmount: 11,
      tickColor: '#e9e9e9'
    }
  })
}

export const buildScatterOptions = (props = {}) => {
  // the reader's percentage precision, handed to every chart from the container
  const percentDecimals = props.reportPercentDecimals

  const {
    answer = {},
    answerVariants: rawAV = [],
    colorTheme = [],
    chartLegendMode,
    sortOrder = 'idesc',
    xAxisTitle = '',
    yAxisTitle = 'respondents',
    width,
    height = CHART_HEIGHT
  } = props

  const data = buildScatterIngot()

  const answerVariants = sortData(answer, [...rawAV], sortOrder, {
    paramPath: { time: ['y', 'rt'] }
  })

  const avPositions = rawAV.filter(av => av != null).map(av => av.position)

  const series = []

  for (const av of answerVariants) {
    const k = `a${av.position}`
    const avAnswer = answer[k]

    if (!avAnswer?.y) continue

    const avColorIndex = avPositions.indexOf(av.position)
    const ans = avAnswer.y
    const avText = removeLogicAndTruncate(av.text, 100)
    const prefixText = `A${av.position}`
    const percents = ans.percents

    const sdata = {
      id: av.position,
      name: chartLegendMode === 'short' ? prefixText : removeLogicAndTruncate(`${prefixText}: ${av.text}`, 27),
      colorIndex: avColorIndex,
      color: colorTheme[avColorIndex % colorTheme.length],
      data: []
    }

    if (ans.rt && ans.amount && percents) {
      const amount = ans.amount
      const tooltipText = `
        <div class='highcharts-tooltip-text'><b>${prefixText}</b>: ${avText}</div>
        <div><b>${ans.rt.toFixed(2)}</b> seconds</div>
        <div><b>${percents.toFixed(percentDecimals ?? 1)}</b>%</div>
        <div><b>${formatAmounts(amount)}</b> ${pluralize(amount, 'respondent', 'respondents')}</div>
      `
      sdata.data.push({
        // the id the answer's table row carries, so a row and its point can find each other
        id: k,
        tooltip: tooltipText,
        amount,
        x: ans.rt,
        y: percents,
        name: avText
      })
    } else {
      sdata.visible = false
    }

    series.push(sdata)
  }

  data.series = series
  data.xAxis.title.text = xAxisTitle
  data.yAxis.title.text = yAxisTitle

  if (data.chart) {
    data.chart.width = width
    data.chart.height = height
  }

  return { options: data }
}
